import { css } from '@emotion/react';
import { PhoneHoursScheduleRule } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { dayOfWeekMap, groupByDayOfWeek } from '../../utils/dates';

// Helper function to format time from 24-hour format to 12-hour format
const formatTime = (time: string) => {
  // Return time in following format: 1:00pm
  return dayjs(time, 'HH:mm:ss').format('h:mma');
};

export const DayHoursList = (props: {
  phoneHours: PhoneHoursScheduleRule[];
  useLargerFonts?: boolean;
  isSmallMode?: boolean;
}) => {
  const dayHours = groupByDayOfWeek(props.phoneHours);
  const orderedDays = [1, 2, 3, 4, 5, 6, 0]; // Monday to Sunday

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing(2)};
      `}
    >
      {orderedDays.map((day) => {
        const { openStartTime, openEndTime, breaks } = dayHours[day] || {};
        return (
          <DayHoursListItem
            key={day}
            dayOfWeek={day}
            openStartTime={openStartTime}
            openEndTime={openEndTime}
            breaks={breaks}
            useLargerFonts={props.useLargerFonts}
          />
        );
      })}
    </div>
  );
};

const DayHoursListItem = ({
  dayOfWeek,
  openStartTime,
  openEndTime,
  breaks,
  useLargerFonts,
}: {
  dayOfWeek: number;
  openStartTime: string;
  openEndTime: string;
  breaks?: { startTime: string; endTime: string; name: string }[];
  useLargerFonts?: boolean;
}) => {
  const { t } = useTranslation('phone');

  return (
    <article
      key={dayOfWeek}
      css={css`
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing(2)};
        padding: ${theme.spacing(2)};
        border-radius: ${theme.borderRadius.small};
        border: 1px solid ${theme.colors.neutral20};
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
        `}
      >
        <Text weight='bold' size={useLargerFonts ? 'large' : 'medium'}>
          {dayOfWeekMap[dayOfWeek as keyof typeof dayOfWeekMap]}
        </Text>
        {!!openStartTime ? (
          <Text size={useLargerFonts ? 'large' : 'medium'}>
            {formatTime(openStartTime)} - {formatTime(openEndTime)}
          </Text>
        ) : (
          <Text size={useLargerFonts ? 'large' : 'medium'}>{t('Closed')}</Text>
        )}
      </div>

      {!!breaks?.length && (
        <div
          css={css`
            background-color: ${theme.colors.indigo10};
            border-radius: ${theme.borderRadius.small};
            padding: ${theme.spacing(2)};
          `}
        >
          <Text
            size={useLargerFonts ? 'medium' : 'small'}
            css={css`
              color: ${theme.colors.neutral70};
              border-bottom: 1px solid ${theme.colors.neutral20};
              padding-bottom: ${theme.spacing(2)};
            `}
          >
            {t('Breaks')}
          </Text>
          {breaks
            ?.sort((a, b) => dayjs(a.startTime, 'HH:mm:ss').diff(dayjs(b.startTime, 'HH:mm:ss')))
            .map(({ startTime, endTime, name }) => (
              <div
                key={name}
                css={css`
                  display: flex;
                  justify-content: space-between;
                  padding-top: ${theme.spacing(2)};
                `}
              >
                <Text size={useLargerFonts ? 'large' : 'medium'}>{name}</Text>
                <Text size={useLargerFonts ? 'large' : 'medium'}>
                  {formatTime(startTime)} - {formatTime(endTime)}
                </Text>
              </div>
            ))}
        </div>
      )}
    </article>
  );
};
